"use client";

import ShopPageMenuPromotions from "@/components/shared/menu-promotions/shop-menu-promotions";
import { ProductGroup } from "@/lib/shop/shop-types";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import { useMemo } from "react";

type GroupHeaderProps = {
    group: ProductGroup;
    className?: string;
};

export default function GroupHeader({ group, className = "" }: GroupHeaderProps) {
    // Memoize the image component
    const imageComponent = useMemo(() => {
        if (!group.imageURL) return null;

        return (
            <a
                href={`brand-list/${group.supplierID}`}
                className="text-center text-sm text-gray-400"
            >
                <div className="flex flex-col items-center justify-center gap-1">
                    <div className="relative h-40 w-48">
                        <Image
                            src={group.imageURL}
                            alt={group.header}
                            fill
                            className="object-contain"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            priority={true}
                        />
                    </div>
                </div>
            </a>
        );
    }, [group.imageURL, group.header, group.supplierID]);

    // Memoize the header text
    const headerText = useMemo(() => {
        if (group.imageURL) return null;
        return <h2 className="p-4 text-2xl font-semibold text-gray-900">{group.header}</h2>;
    }, [group.imageURL, group.header]);

    // Memoize the promotions carousel
    const promotionsCarousel = useMemo(() => {
        if (group.type !== "supplier") {
            return null;
        }

        return (
            <div className="m-4 flex h-full w-full flex-col items-center justify-around">
                <div className="mb-2 text-center text-lg text-accent-foreground">
                    {group.subHeader}
                </div>
                <ShopPageMenuPromotions
                    supplierIDs={[group.supplierID!]}
                    state={group.state}
                    showTitle={false}
                />
                <a
                    href={`brand-list/${group.supplierID}`}
                    className="mt-4 text-gray-400 hover:text-blue-500 hover:underline"
                >
                    <div className="flex flex-row items-center gap-1 text-sm ">
                        Learn more about {group.header}{" "}
                        <ArrowRight className="inline-block" size={16} />
                    </div>
                </a>
            </div>
        );
    }, [group.type, group.supplierID, group.state, group.subHeader, group.header]);

    // Memoize the main container class
    const containerClassName = useMemo(() => {
        return `relative mb-6 w-full ${className}`;
    }, [className]);

    return (
        <div className={`${containerClassName} rounded-lg border bg-white p-2`}>
            <div className="flex flex-col items-center gap-6 md:flex-row md:justify-between">
                <div className="ml-4 items-center">{imageComponent || headerText}</div>
                {promotionsCarousel}
            </div>
        </div>
    );
}
